import { waitForPendingHides, showModal, hideModal } from "./Modals";
/**
 * Bridges server-rendered Bootstrap modals (opened via `data-bs-toggle="modal"`)
 * with the OverlayStack using the generic `"modal"` route.
 *
 * Two-way sync:
 * - User clicks `data-bs-toggle` → Bootstrap fires `show.bs.modal` → push to stack
 * - Back button → stack pops → bridge hides Bootstrap modal
 * - Forward button → stack restores → bridge shows Bootstrap modal
 * - User dismiss (Escape / backdrop) → `hidden.bs.modal` → pop from stack
 *
 * Only intercepts modals opened via toggle elements (`event.relatedTarget` is set),
 * not programmatic `modal.show()` calls from OverlayModal or OverlayView.
 */
export class BootstrapModalBridge {
    constructor(overlayStack) {
        this.overlayStack = overlayStack;
        this.activeModalId = null;
        /** True while handleBootstrapShow is pushing to the stack, so handleStackChange skips the redundant show call. */
        this.isPushingFromShow = false;
        this.disposeSubscription = null;
        /**
         * Reacts to overlay stack changes. When `modal` is the top entry, shows
         * the modal (skipping the redundant show when data-bs-toggle initiated it).
         * When `modal` is no longer the top, hides the active modal.
         */
        this.handleStackChange = () => {
            var _a;
            const entry = this.overlayStack.find("modal");
            if (((_a = this.overlayStack.top) === null || _a === void 0 ? void 0 : _a.route) === "modal" && entry) {
                this.activeModalId = entry.params.id;
                if (!this.isPushingFromShow) {
                    this.showBsModal(entry.params.id);
                }
            }
            else if (this.activeModalId !== null) {
                this.hideBsModal(this.activeModalId);
                this.activeModalId = null;
            }
        };
        /**
         * Bootstrap is opening a modal via `data-bs-toggle`.
         * Push onto overlay stack unless already there.
         *
         * `relatedTarget` is the toggle element for `data-bs-toggle` opens,
         * absent for programmatic `modal.show()` calls — we only handle the former.
         */
        this.handleBootstrapShow = (event) => {
            // Bootstrap's show.bs.modal sets relatedTarget to the toggle element
            // for data-bs-toggle opens; absent for programmatic modal.show() calls.
            // The event is a Bootstrap.Event with relatedTarget, not a native Event.
            const bsEvent = event;
            if (bsEvent.relatedTarget == null)
                return;
            const modalEl = event.target;
            if (!modalEl || !(modalEl === null || modalEl === void 0 ? void 0 : modalEl.id))
                return;
            if (this.overlayStack.has("modal"))
                return;
            // Skip modals managed by OverlayModal — they have their own
            // overlay route and would cause a duplicate stack entry.
            if (modalEl.hasAttribute("data-overlay-managed"))
                return;
            // Let Bootstrap's show() complete normally (no preventDefault).
            // Set isPushingFromShow so the MobX reaction (which fires synchronously
            // from stack.push) skips the redundant showModal() call. Without this
            // guard, showModal → modal.show() would run a second time, causing
            // ScrollBarHelper.hide() to save "overflow: hidden" as original and
            // never restore scrolling on close.
            this.isPushingFromShow = true;
            try {
                this.overlayStack.push("modal", { id: modalEl.id });
            }
            finally {
                this.isPushingFromShow = false;
            }
        };
        /**
         * Bootstrap modal was hidden. If user-initiated (Escape / backdrop),
         * pop from the stack so OverlayHistorySync pushes the new URL without the modal param.
         */
        this.handleBootstrapHidden = (event) => {
            var _a;
            const modalEl = event.target;
            if (!(modalEl === null || modalEl === void 0 ? void 0 : modalEl.id) || modalEl.id !== this.activeModalId)
                return;
            // Programmatic hide (reaction-driven) — isVisible is already false
            if (((_a = this.overlayStack.top) === null || _a === void 0 ? void 0 : _a.route) !== "modal")
                return;
            // User-initiated dismiss
            this.overlayStack.pop();
        };
    }
    start() {
        document.addEventListener("show.bs.modal", this.handleBootstrapShow);
        document.addEventListener("hidden.bs.modal", this.handleBootstrapHidden);
        this.disposeSubscription = this.overlayStack.subscribe(this.handleStackChange);
    }
    stop() {
        var _a;
        document.removeEventListener("show.bs.modal", this.handleBootstrapShow);
        document.removeEventListener("hidden.bs.modal", this.handleBootstrapHidden);
        (_a = this.disposeSubscription) === null || _a === void 0 ? void 0 : _a.call(this);
        this.disposeSubscription = null;
    }
    /**
     * Show a Bootstrap modal by id.
     * Waits for any currently-hiding modals to finish first to prevent
     * simultaneous show/hide animations (visual glitch).
     */
    showBsModal(id) {
        void waitForPendingHides().then(() => {
            const el = document.getElementById(id);
            if (el)
                showModal(el);
        });
    }
    /** Hide a Bootstrap modal by id. */
    hideBsModal(id) {
        const el = document.getElementById(id);
        if (el)
            hideModal(el);
    }
}
